// Shift reports — Phase 4 (2026-10-04).
//
// Nurses author end-of-shift handovers; doctors + everyone with the tab
// read them. Admins can edit any report; the author can edit their own.
// Pure list + compose + inline edit — no @mentions, no attachments,
// nothing fancy. The patient_ids_snapshot column exists so a report
// freezes which patients were on-shift at author time, but v1 UI leaves
// that as a free-text summary (the snapshot stays null here).

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ShiftReportView } from '@pamosi/shared';
import { ApiError, clinicApi, shiftReportsApi } from '../lib/api';
import { useAuth } from '../lib/auth';

export function ShiftReportsScreen() {
    const { data: myClinic } = useQuery({
        queryKey: ['my-clinic'],
        queryFn: () => clinicApi.myClinic(),
    });
    const role = myClinic?.member?.role ?? null;
    const canWrite = role === 'doctor' || role === 'nurse' || role === 'provider';

    const listQ = useQuery({
        queryKey: ['shift-reports'],
        queryFn: () => shiftReportsApi.list(),
    });

    const [composing, setComposing] = useState(false);

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h1 className="text-slate-900 text-2xl font-semibold">Shift reports</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        End-of-shift handover notes. Nurses author; everyone with the tab reads.
                    </p>
                </div>
                {canWrite && !composing && (
                    <button
                        type="button"
                        className="pm-btn-primary"
                        onClick={() => setComposing(true)}
                    >
                        New report
                    </button>
                )}
            </div>

            {composing && (
                <ComposeShiftReport
                    onDone={() => setComposing(false)}
                    onCancel={() => setComposing(false)}
                />
            )}

            {listQ.isLoading && (
                <div className="pm-card p-8 text-center text-slate-500 text-sm">Loading…</div>
            )}
            {listQ.isError && (
                <div className="pm-card p-8 text-center text-rose-700 text-sm">
                    {listQ.error instanceof ApiError ? listQ.error.message : 'Could not load shift reports.'}
                </div>
            )}
            {listQ.data && listQ.data.reports.length === 0 && !composing && (
                <div className="pm-card p-8 text-center text-slate-500 text-sm">
                    No shift reports yet.
                </div>
            )}

            {listQ.data && listQ.data.reports.length > 0 && (
                <ul className="space-y-3">
                    {listQ.data.reports.map((r) => (
                        <li key={r.id}>
                            <ShiftReportRow report={r} role={role} />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function ComposeShiftReport({
    onDone,
    onCancel,
}: {
    onDone: () => void;
    onCancel: () => void;
}) {
    const qc = useQueryClient();
    const [shiftLabel, setShiftLabel] = useState('');
    const [shiftStart, setShiftStart] = useState('');
    const [shiftEnd, setShiftEnd] = useState('');
    const [summary, setSummary] = useState('');

    const mut = useMutation({
        mutationFn: () =>
            shiftReportsApi.create({
                shift_label: shiftLabel.trim(),
                shift_start_at: shiftStart ? new Date(shiftStart).toISOString() : null,
                shift_end_at: shiftEnd ? new Date(shiftEnd).toISOString() : null,
                summary: summary.trim(),
            }),
        onSuccess: async () => {
            await qc.invalidateQueries({ queryKey: ['shift-reports'] });
            onDone();
        },
    });

    const canSubmit = shiftLabel.trim().length > 0 && summary.trim().length > 0 && !mut.isPending;

    return (
        <div className="pm-card p-4 space-y-3">
            <div className="text-slate-900 text-sm font-semibold">New shift report</div>
            <input
                className="pm-input"
                type="text"
                placeholder="Shift label (e.g. Night 2026-10-04)"
                value={shiftLabel}
                onChange={(e) => setShiftLabel(e.target.value)}
                disabled={mut.isPending}
                maxLength={120}
            />
            <div className="grid grid-cols-2 gap-2">
                <label className="text-xs text-slate-600">
                    Shift start
                    <input
                        className="pm-input mt-1"
                        type="datetime-local"
                        value={shiftStart}
                        onChange={(e) => setShiftStart(e.target.value)}
                        disabled={mut.isPending}
                    />
                </label>
                <label className="text-xs text-slate-600">
                    Shift end
                    <input
                        className="pm-input mt-1"
                        type="datetime-local"
                        value={shiftEnd}
                        onChange={(e) => setShiftEnd(e.target.value)}
                        disabled={mut.isPending}
                    />
                </label>
            </div>
            <textarea
                className="pm-input"
                rows={8}
                placeholder="Handover summary — who's on observation, outstanding tasks, concerns for the next shift."
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                disabled={mut.isPending}
                maxLength={10_000}
            />
            <div className="flex items-center justify-end gap-2">
                <button
                    type="button"
                    className="pm-btn-ghost"
                    onClick={onCancel}
                    disabled={mut.isPending}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="pm-btn-primary"
                    onClick={() => mut.mutate()}
                    disabled={!canSubmit}
                >
                    {mut.isPending ? 'Saving…' : 'Post report'}
                </button>
            </div>
            {mut.isError && (
                <div className="pm-error text-xs">
                    {mut.error instanceof ApiError ? mut.error.message : 'Save failed.'}
                </div>
            )}
        </div>
    );
}

function ShiftReportRow({
    report,
    role,
}: {
    report: ShiftReportView;
    role: string | null;
}) {
    const { clinician } = useAuth();
    const isAuthor = !!clinician && clinician.id === report.author_clinician_id;
    const isAdmin = role === 'admin';
    const canEdit = isAuthor || isAdmin;

    const [editing, setEditing] = useState(false);

    const whenRange = useMemo(() => {
        if (!report.shift_start_at && !report.shift_end_at) return null;
        const start = report.shift_start_at ? new Date(report.shift_start_at).toLocaleString() : '?';
        const end = report.shift_end_at ? new Date(report.shift_end_at).toLocaleString() : '?';
        return `${start} → ${end}`;
    }, [report.shift_start_at, report.shift_end_at]);

    if (editing) {
        return (
            <EditShiftReport
                report={report}
                onDone={() => setEditing(false)}
                onCancel={() => setEditing(false)}
            />
        );
    }

    return (
        <div className="pm-card p-4 space-y-2">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-slate-900 text-sm font-semibold">
                        {report.shift_label}
                    </div>
                    <div className="text-slate-500 text-xs mt-0.5">
                        {report.author_full_name ?? 'Unknown author'}
                        {' · posted '}
                        {new Date(report.created_at).toLocaleString()}
                        {report.updated_at !== report.created_at && ' (edited)'}
                    </div>
                    {whenRange && (
                        <div className="text-slate-500 text-xs mt-0.5">
                            Shift: {whenRange}
                        </div>
                    )}
                </div>
                {canEdit && (
                    <button
                        type="button"
                        className="pm-btn-ghost pm-btn-sm"
                        onClick={() => setEditing(true)}
                    >
                        Edit
                    </button>
                )}
            </div>
            <div className="text-slate-800 text-sm whitespace-pre-wrap">
                {report.summary}
            </div>
        </div>
    );
}

function EditShiftReport({
    report,
    onDone,
    onCancel,
}: {
    report: ShiftReportView;
    onDone: () => void;
    onCancel: () => void;
}) {
    const qc = useQueryClient();
    const [shiftLabel, setShiftLabel] = useState(report.shift_label);
    const [shiftStart, setShiftStart] = useState(
        report.shift_start_at ? toLocalInput(report.shift_start_at) : '',
    );
    const [shiftEnd, setShiftEnd] = useState(
        report.shift_end_at ? toLocalInput(report.shift_end_at) : '',
    );
    const [summary, setSummary] = useState(report.summary);

    const mut = useMutation({
        mutationFn: () =>
            shiftReportsApi.update(report.id, {
                shift_label: shiftLabel.trim(),
                shift_start_at: shiftStart ? new Date(shiftStart).toISOString() : null,
                shift_end_at: shiftEnd ? new Date(shiftEnd).toISOString() : null,
                summary: summary.trim(),
            }),
        onSuccess: async () => {
            await qc.invalidateQueries({ queryKey: ['shift-reports'] });
            onDone();
        },
    });

    const canSubmit = shiftLabel.trim().length > 0 && summary.trim().length > 0 && !mut.isPending;

    return (
        <div className="pm-card p-4 space-y-3">
            <div className="text-slate-900 text-sm font-semibold">Edit shift report</div>
            <input
                className="pm-input"
                type="text"
                value={shiftLabel}
                onChange={(e) => setShiftLabel(e.target.value)}
                disabled={mut.isPending}
                maxLength={120}
            />
            <div className="grid grid-cols-2 gap-2">
                <label className="text-xs text-slate-600">
                    Shift start
                    <input
                        className="pm-input mt-1"
                        type="datetime-local"
                        value={shiftStart}
                        onChange={(e) => setShiftStart(e.target.value)}
                        disabled={mut.isPending}
                    />
                </label>
                <label className="text-xs text-slate-600">
                    Shift end
                    <input
                        className="pm-input mt-1"
                        type="datetime-local"
                        value={shiftEnd}
                        onChange={(e) => setShiftEnd(e.target.value)}
                        disabled={mut.isPending}
                    />
                </label>
            </div>
            <textarea
                className="pm-input"
                rows={8}
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                disabled={mut.isPending}
                maxLength={10_000}
            />
            <div className="flex items-center justify-end gap-2">
                <button
                    type="button"
                    className="pm-btn-ghost"
                    onClick={onCancel}
                    disabled={mut.isPending}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="pm-btn-primary"
                    onClick={() => mut.mutate()}
                    disabled={!canSubmit}
                >
                    {mut.isPending ? 'Saving…' : 'Save changes'}
                </button>
            </div>
            {mut.isError && (
                <div className="pm-error text-xs">
                    {mut.error instanceof ApiError ? mut.error.message : 'Save failed.'}
                </div>
            )}
        </div>
    );
}

// datetime-local wants "YYYY-MM-DDTHH:mm" with no timezone suffix.
function toLocalInput(iso: string): string {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
